import { RunElement } from '../../shared/run-element.js';
import { skinResolutionMixin } from '../../shared/skin-resolution-mixin.js';
import * as defaultSkin from './skins/radio.default.js';

// Module-level counter for stable per-instance shadow ids (description wiring).
let radioIdCounter = 0;

class RunRadio extends skinResolutionMixin(RunElement) {
  #checked = false;
  #value = '';
  #name = '';
  #disabled = false;
  #required = false;
  #invalid = false;
  #readonly = false;
  #descriptionObserver = null;

  static defaultSkin = defaultSkin;

  static skinLoaders = {};

  // Form participation is owned entirely by the enclosing run-radio-group
  // (contract §Form Association) — this primitive never attaches internals.
  static formAssociated = false;

  static get observedAttributes() {
    return ['checked', 'value', 'name', 'disabled', 'required', 'invalid', 'readonly'];
  }

  _onClickBound = (e) => this.#handleClick(e);
  _onKeydownBound = (e) => this.#handleKeydown(e);
  _onFocusBound = () => this.#emit('run-radio-focus', {});
  _onBlurBound = () => this.#emit('run-radio-blur', {});

  constructor() {
    super();
    const n = ++radioIdCounter;
    this._labelId = `run-radio-label-${n}`;
    this._descId = `run-radio-desc-${n}`;
    this._descriptionSlotFilled = false;
  }

  connectedCallback() {
    super.connectedCallback();
    this.setAttribute('role', 'radio');
    this.addEventListener('click', this._onClickBound);
    this.addEventListener('keydown', this._onKeydownBound);
    this.addEventListener('focus', this._onFocusBound);
    this.addEventListener('blur', this._onBlurBound);
    this.#syncDescriptionSlot();
    this.#descriptionObserver = new MutationObserver(() => this.#syncDescriptionSlot());
    this.#descriptionObserver.observe(this, { childList: true });
    this.#updateAria();
    this.requestUpdate();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this._onClickBound);
    this.removeEventListener('keydown', this._onKeydownBound);
    this.removeEventListener('focus', this._onFocusBound);
    this.removeEventListener('blur', this._onBlurBound);
    this.#descriptionObserver?.disconnect();
    this.#descriptionObserver = null;
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch (name) {
      case 'checked':  this.checked = newValue !== null; break;
      case 'value':    this.value = newValue !== null ? newValue : ''; break;
      case 'name':     this.name = newValue !== null ? newValue : ''; break;
      case 'disabled': this.disabled = newValue !== null; break;
      case 'required': this.required = newValue !== null; break;
      case 'invalid':  this.invalid = newValue !== null; break;
      case 'readonly': this.readonly = newValue !== null; break;
    }
  }

  get checked() { return this.#checked; }
  set checked(value) {
    const coerced = Boolean(value);
    if (this.#checked === coerced) return;
    this.#checked = coerced;
    this.toggleAttribute('checked', coerced);
    this.#updateAria();
    this.requestUpdate();
  }

  get value() { return this.#value; }
  set value(value) {
    const coerced = value == null ? '' : String(value).trim();
    if (this.#value === coerced) return;
    this.#value = coerced;
    this.setAttribute('value', coerced);
    this.requestUpdate();
  }

  get name() { return this.#name; }
  set name(value) {
    const coerced = value == null ? '' : String(value).trim();
    if (this.#name === coerced) return;
    this.#name = coerced;
    if (coerced) {
      this.setAttribute('name', coerced);
    } else {
      this.removeAttribute('name');
    }
    this.requestUpdate();
  }

  get disabled() { return this.#disabled; }
  set disabled(value) {
    const coerced = Boolean(value);
    if (this.#disabled === coerced) return;
    this.#disabled = coerced;
    this.toggleAttribute('disabled', coerced);
    this.#updateAria();
    this.requestUpdate();
  }

  get required() { return this.#required; }
  set required(value) {
    const coerced = Boolean(value);
    if (this.#required === coerced) return;
    this.#required = coerced;
    this.toggleAttribute('required', coerced);
    this.#updateAria();
    this.requestUpdate();
  }

  get invalid() { return this.#invalid; }
  set invalid(value) {
    const coerced = Boolean(value);
    if (this.#invalid === coerced) return;
    this.#invalid = coerced;
    this.toggleAttribute('invalid', coerced);
    this.#updateAria();
    this.requestUpdate();
  }

  get readonly() { return this.#readonly; }
  set readonly(value) {
    const coerced = Boolean(value);
    if (this.#readonly === coerced) return;
    this.#readonly = coerced;
    this.toggleAttribute('readonly', coerced);
    this.#updateAria();
    this.requestUpdate();
  }

  updated(changedProperties) {
    super.updated?.(changedProperties);
    this.#updateAria();
  }

  // ARIA states are synchronously updated in property setters (no batching)
  // and re-asserted after every skin render.
  #updateAria() {
    this.setAttribute('aria-checked', String(this.#checked));
    if (this.#disabled) {
      this.setAttribute('aria-disabled', 'true');
    } else {
      this.removeAttribute('aria-disabled');
    }
    if (this.#required) {
      this.setAttribute('aria-required', 'true');
    } else {
      this.removeAttribute('aria-required');
    }
    if (this.#invalid) {
      this.setAttribute('aria-invalid', 'true');
    } else {
      this.removeAttribute('aria-invalid');
    }
    if (this.#readonly) {
      this.setAttribute('aria-readonly', 'true');
    } else {
      this.removeAttribute('aria-readonly');
    }
    if (this._descriptionSlotFilled) {
      this.setAttribute('aria-describedby', this._descId);
    } else {
      this.removeAttribute('aria-describedby');
    }
    this.setAttribute('tabindex', this.#disabled ? '-1' : '0');
  }

  #syncDescriptionSlot() {
    const filled = this.querySelector(':scope > [slot="description"]') !== null;
    if (this._descriptionSlotFilled === filled) return;
    this._descriptionSlotFilled = filled;
    this.#updateAria();
    this.requestUpdate();
  }

  #handleClick() {
    this.#select();
  }

  #handleKeydown(event) {
    switch (event.key) {
      case ' ':
        // preventDefault unconditionally: Space must never scroll the page
        // while a radio has focus, even when activation is guarded.
        event.preventDefault();
        this.#select();
        break;
      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault();
        this.#focusNextInGroup(1);
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault();
        this.#focusNextInGroup(-1);
        break;
    }
  }

  // Activation guards (contract): no-op when disabled, readonly, or already
  // checked. Programmatic property assignment never routes through here, so
  // it never emits events.
  #select() {
    if (this.#disabled || this.#readonly || this.#checked) return;
    this.#getGroupSiblings().forEach(sibling => {
      if (sibling !== this && sibling.checked) {
        sibling.checked = false;
      }
    });
    this.checked = true;
    // Invariant: run-radio-input fires strictly before run-radio-change.
    this.#emit('run-radio-input', { value: this.#value });
    this.#emit('run-radio-change', { checked: true, value: this.#value });
  }

  #focusNextInGroup(direction) {
    if (this.#disabled) return;
    const siblings = this.#getGroupSiblings();
    if (siblings.length <= 1) return;
    const currentIndex = siblings.indexOf(this);
    let nextIndex = currentIndex + direction;
    if (nextIndex < 0) nextIndex = siblings.length - 1;
    if (nextIndex >= siblings.length) nextIndex = 0;
    const target = siblings[nextIndex];
    if (target && target !== this) {
      target.focus();
      target.#select();
    }
  }

  #getGroupSiblings() {
    if (!this.#name) return [this];
    const root = this.getRootNode();
    const candidates = root.querySelectorAll
      ? Array.from(root.querySelectorAll(`run-radio[name="${CSS.escape(this.#name)}"]`))
      : [this];
    return candidates.filter(el => !el.disabled);
  }

  #emit(name, detail) {
    this.dispatchEvent(new CustomEvent(name, {
      bubbles: true,
      composed: true,
      detail,
    }));
  }
}

if (!customElements.get('run-radio')) {
  customElements.define('run-radio', RunRadio);
}

export { RunRadio };
export default RunRadio;
